import React, { type JSX } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, DocumentIcon, TableCellsIcon, PhotoIcon } from '@heroicons/react/24/outline';
import UploadProgressBar from './UploadProgressBar_Component';
import { formatFileSize } from './utils/formatFileSize';

export interface UploadItem {
  id: string;
  fileName?: string;
  fileSize?: number;
  progress?: number;
  status?: string;
  completed?: boolean;
  error?: boolean;
}

interface UploadTableProps {
  uploads?: UploadItem[];
}

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const ICON_BY_EXTENSION: Record<string, { Icon: IconComponent; colour: string }> = {
  pdf: { Icon: DocumentIcon, colour: 'text-red-600' },
  xlsx: { Icon: TableCellsIcon, colour: 'text-green-600' },
  xls: { Icon: TableCellsIcon, colour: 'text-green-600' },
  csv: { Icon: TableCellsIcon, colour: 'text-green-600' },
  png: { Icon: PhotoIcon, colour: 'text-blue-600' },
  jpg: { Icon: PhotoIcon, colour: 'text-blue-600' },
  jpeg: { Icon: PhotoIcon, colour: 'text-blue-600' },
  gif: { Icon: PhotoIcon, colour: 'text-blue-600' },
};

const DEFAULT_FILE_ICON = { Icon: DocumentIcon, colour: 'text-gray-600' };

/**
 * Get appropriate icon for file based on extension
 */
const getFileIcon = (fileName?: string): JSX.Element => {
  // Handle missing or invalid fileName
  const extension =
    fileName && typeof fileName === 'string' ? fileName.split('.').pop()?.toLowerCase() : undefined;
  const { Icon, colour } = (extension && ICON_BY_EXTENSION[extension]) || DEFAULT_FILE_ICON;

  return <Icon className={`w-4 h-4 flex-shrink-0 ${colour}`} />;
};

interface StatusDisplay {
  text: string;
  className: string;
}

/** Ordered rules mapping a raw status string onto its display label. */
const STATUS_RULES: Array<{ matches: (status: string) => boolean; display: StatusDisplay }> = [
  // Backend processing statuses
  {
    matches: (s) => s.includes('Processing document') || s === 'Processing',
    display: { text: 'Processing', className: 'text-purple-600' },
  },
  { matches: (s) => s === 'Ready', display: { text: 'Ready', className: 'text-green-600' } },
  // Upload phase statuses
  {
    matches: (s) => s.includes('Waiting for processing'),
    display: { text: 'Waiting', className: 'text-gray-600' },
  },
  {
    matches: (s) => s.includes('Uploading') || s === 'uploading',
    display: { text: 'Uploading', className: 'text-blue-600' },
  },
  {
    matches: (s) => s.includes('Preparing') || s.includes('Getting upload URL'),
    display: { text: 'Preparing', className: 'text-gray-600' },
  },
];

/**
 * Get status display text and color
 */
const getStatusDisplay = (status?: string, completed?: boolean, error?: boolean): StatusDisplay => {
  if (error) {
    return { text: 'Failed', className: 'text-red-600' };
  }

  if (completed) {
    return { text: 'Complete', className: 'text-green-600' };
  }

  const rule = status ? STATUS_RULES.find(({ matches }) => matches(status)) : undefined;

  return rule ? rule.display : { text: status || 'Uploading', className: 'text-gray-600' };
};

type UploadStatus = 'uploading' | 'processing' | 'completed' | 'error';

/** Map an upload onto the progress bar's four-state vocabulary. */
const getUploadStatus = (upload: UploadItem, statusText: string): UploadStatus => {
  if (upload.error) return 'error';
  if (upload.completed) return 'completed';
  if (statusText === 'Processing' || statusText === 'Waiting') return 'processing';
  return 'uploading';
};

function StatusCell({
  upload,
  uploadStatus,
  statusText,
  statusClass,
}: {
  upload: UploadItem;
  uploadStatus: UploadStatus;
  statusText: string;
  statusClass: string;
}): JSX.Element {
  return (
    <div className={`flex items-center gap-1 min-w-[100px] justify-end`}>
      {upload.completed && !upload.error && (
        <CheckCircleIcon className="w-4 h-4 flex-shrink-0 text-green-600" />
      )}
      {upload.error && <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0 text-red-600" />}
      <span className={`text-sm ${statusClass}`}>{statusText}</span>
      {uploadStatus === 'uploading' && (upload.progress || 0) > 0 && (
        <span className="text-sm text-gray-500 ml-1">({upload.progress}%)</span>
      )}
    </div>
  );
}

/** A single file row: icon, name, size, progress and status. */
function UploadRow({ upload }: { upload: UploadItem }): JSX.Element {
  const { text: statusText, className: statusClass } = getStatusDisplay(
    upload.status,
    upload.completed,
    upload.error
  );
  const uploadStatus = getUploadStatus(upload, statusText);

  return (
    <div className="px-4 py-3 hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-4">
        {/* File Icon and Name */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {getFileIcon(upload.fileName)}
          <span
            className="text-sm text-gray-900 truncate"
            title={upload.fileName || 'Uploading...'}
          >
            {upload.fileName || 'Uploading...'}
          </span>
        </div>

        {/* File Size */}
        <div className="text-sm text-gray-500 min-w-[60px] text-right">
          {upload.fileSize ? formatFileSize(upload.fileSize) : '-'}
        </div>

        {/* Progress Bar */}
        <div className="flex-1 max-w-[200px]">
          <UploadProgressBar
            progress={upload.progress || 0}
            status={uploadStatus}
            showPercentage={false}
          />
        </div>

        {/* Status with Icon */}
        <StatusCell
          upload={upload}
          uploadStatus={uploadStatus}
          statusText={statusText}
          statusClass={statusClass}
        />
      </div>
      {/* Error detail message */}
      {upload.error && upload.status && upload.status !== 'error' && (
        <p className="text-xs text-red-500 mt-1 pl-6 truncate" title={upload.status}>
          {upload.status}
        </p>
      )}
    </div>
  );
}

/**
 * Upload Table Component - displays multiple file uploads in a clean table format
 */
const UploadTable: React.FC<UploadTableProps> = ({ uploads = [] }) => {
  // Don't render if no uploads
  if (!uploads || uploads.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-white rounded-lg shadow-sm">
      {/* Table Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-medium text-gray-900">Files ({uploads.length})</h3>
      </div>

      {/* Table Content */}
      <div className="divide-y divide-gray-50">
        {uploads.map((upload, index) => (
          <UploadRow key={upload.id || index} upload={upload} />
        ))}
      </div>
    </div>
  );
};

export default UploadTable;
